'use client';
import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { ArrowLeft, Star, MapPin, Globe, Lock, Share2 } from 'lucide-react';
import { MOCK_CAFES } from '@/data/mockCafes';
import type { Board } from './SimpanClient';

interface Props {
  board: Board;
  onBack: () => void;
}

export default function BoardDetail({ board, onBack }: Props) {
  // Backend: Replace with Supabase query: collection_items.select('place_id').eq('collection_id', board.id)
  const cafes = MOCK_CAFES.filter((c) => board.cafeIds.includes(c.id));
  const allCafes = cafes.length > 0 ? cafes : MOCK_CAFES.slice(0, 6);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 pt-6 pb-16">
      {/* Back */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm font-600 text-muted-foreground hover:text-primary transition-colors mb-6"
      >
        <ArrowLeft size={16} />
        Kembali ke Koleksi
      </button>

      {/* Board Header */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-800 text-primary mb-1">{board.title}</h1>
          {board.description && (
            <p className="text-sm text-muted-foreground mb-3">{board.description}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              {board.isPublic ? <Globe size={12} className="text-green-DEFAULT" /> : <Lock size={12} />}
              {board.isPublic ? 'Publik' : 'Privat'}
            </span>
            <span className="flex items-center gap-1">
              <MapPin size={12} className="text-accent" />
              <span className="font-700 text-foreground font-tabular">{allCafes.length}</span> tempat
            </span>
          </div>
        </div>
        <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-card text-sm font-600 text-primary hover:border-accent hover:text-accent transition-colors">
          <Share2 size={14} />
          Bagikan
        </button>
      </div>

      {/* Masonry Grid */}
      {allCafes.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-4xl mb-3">☕</div>
          <p className="text-base font-700 text-primary mb-2">Board ini masih kosong</p>
          <p className="text-sm text-muted-foreground mb-5">Jelajahi cafe dan tap bookmark untuk menambahkan ke board ini.</p>
          <Link href="/jelajahi-explore" className="btn-primary inline-flex">Jelajahi Cafe</Link>
        </div>
      ) : (
        <div className="masonry-2 lg:masonry-3">
          {allCafes.map((cafe) => (
            <Link
              key={`bd-${cafe.id}`}
              href={`/coffeshop-detail-page?id=${cafe.id}`}
              className="masonry-item place-card block"
            >
              <div className="relative overflow-hidden" style={{ aspectRatio: cafe.id.charCodeAt(5) % 2 === 0 ? '4/3' : '3/4' }}>
                <AppImage
                  src={cafe.photos[0]}
                  alt={`Foto ${cafe.name} di board ${board.title}`}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 50vw, 33vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white text-sm font-700 line-clamp-1">{cafe.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-white/80 text-xs">{cafe.area}</span>
                    <div className="flex items-center gap-0.5 ml-auto">
                      <Star size={10} className="text-yellow-300 fill-current" />
                      <span className="text-white/90 text-xs font-700 font-tabular">{cafe.rating}</span>
                    </div>
                  </div>
                </div>
                <div className="absolute top-2 left-2">
                  <span className={cafe.isOpen ? 'open-badge' : 'closed-badge'} style={{ fontSize: '9px', padding: '2px 6px' }}>
                    {cafe.isOpen ? 'Buka' : 'Tutup'}
                  </span>
                </div>
              </div>
              <div className="p-3">
                <div className="flex flex-wrap gap-1">
                  {cafe.vibeTags.slice(0, 2).map((tag) => (
                    <span key={`bdtag-${cafe.id}-${tag}`} className="tag-pill text-[10px]">{tag}</span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5 font-tabular">
                  Rp{(cafe.priceMin / 1000).toFixed(0)}K – Rp{(cafe.priceMax / 1000).toFixed(0)}K
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}